import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ViewChild,
  booleanAttribute,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DsIconComponent, DsIconSize } from '../icons/ds-icon.component';
import { DsIconName } from '../icons/ds-icons';
import { DsLoadingSpinnerComponent } from '../progress/ds-loading-spinner.component';

export type DsButtonEmphasis =
  | 'primary'
  | 'primary-alt'
  | 'secondary'
  | 'secondary-outlined'
  | 'secondary-filled'
  | 'terciary';
export type DsCanonicalButtonEmphasis = Exclude<DsButtonEmphasis, 'secondary'>;
export type DsButtonSize = 'xs' | 's' | 'm';

/** Keeps the legacy API value on the exact same rendering path as secondary-outlined. */
export function resolveDsButtonEmphasis(
  emphasis: DsButtonEmphasis,
): DsCanonicalButtonEmphasis {
  return emphasis === 'secondary' ? 'secondary-outlined' : emphasis;
}

@Component({
  selector: 'ds-button-v2',
  standalone: true,
  imports: [CommonModule, DsIconComponent, DsLoadingSpinnerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <button
      #buttonElement
      class="ds-btn"
      [class]="hostClasses"
      [style.--ds-button-resolved-max-width]="resolvedMaxWidth"
      [disabled]="disabled || null"
      (click)="clicked.emit($event)"
    >
      <ds-icon
        *ngIf="leadingIcon"
        class="ds-btn__icon"
        [name]="leadingIcon"
        [size]="iconSize"
      />
      <span class="ds-btn__label ds-text--visual-padding-none">{{ label }}</span>
      <ds-loading-spinner
        *ngIf="loading"
        class="ds-btn__spinner"
        ariaLabel="Cargando"
      />
      <ds-icon
        *ngIf="!loading && trailingIcon"
        class="ds-btn__icon"
        [name]="trailingIcon"
        [size]="iconSize"
      />
    </button>
  `,
  styles: [`
    :host { display: contents; }

    .ds-btn {
      position:        relative;
      display:         inline-flex;
      align-items:     center;
      justify-content: center;
      box-sizing:      border-box;
      gap:             var(--ds-button-m-gap);
      border:          none;
      border-radius:   var(--ds-button-resolved-border-radius, var(--ds-button-m-border-radius));
      background:      var(--_button-bg);
      cursor:          pointer;
      font-family:     'Sura Sans', sans-serif;
      font-weight:     var(--ds-button-font-weight);
      white-space:     nowrap;
      overflow:        hidden;
      outline:         none;
      width:           auto;
      max-width:       var(--ds-button-resolved-max-width, var(--ds-button-default-max-width));
      transition:      all 100ms ease-out;
      will-change:     width;
    }

    .ds-btn--full-width {
      width:     100%;
      max-width: var(--ds-button-resolved-max-width, var(--ds-button-default-max-width));
    }

    @property --ds-button-gradient-border-angle {
      syntax: '<angle>';
      inherits: false;
      initial-value: 0deg;
    }

    .ds-btn::before {
      content:       '';
      position:      absolute;
      inset:         0;
      border-radius: inherit;
      background:    var(--_hover-overlay, transparent);
      opacity:       0;
      pointer-events: none;
      transition:    opacity 120ms ease;
    }

    .ds-btn--gradient-border {
      border: var(--ds-button-gradient-border-width) solid transparent;
      background:
        linear-gradient(var(--_gradient-hover-layer, transparent), var(--_gradient-hover-layer, transparent)) padding-box,
        linear-gradient(var(--_button-bg), var(--_button-bg)) padding-box,
        conic-gradient(
          from var(--ds-button-gradient-border-angle),
          var(--ds-button-gradient-border-start-color) 0deg,
          var(--ds-button-gradient-border-middle-color) 90deg,
          var(--ds-button-gradient-border-end-color) 180deg,
          var(--ds-button-gradient-border-middle-color) 270deg,
          var(--ds-button-gradient-border-start-color) 360deg
        ) border-box;
      animation: ds-button-gradient-border-spin 3000ms linear infinite;
    }

    .ds-btn--gradient-border::before {
      content: none;
    }

    .ds-btn--gradient-border:hover:not(:disabled) {
      --_gradient-hover-layer: var(--_hover-overlay, transparent);
    }

    @keyframes ds-button-gradient-border-spin {
      to { --ds-button-gradient-border-angle: 360deg; }
    }

    .ds-btn > * {
      position: relative;
      z-index:  1;
    }

    .ds-btn:hover:not(:disabled)::before {
      opacity: 1;
    }

    .ds-btn__icon {
      display:     block;
      flex-shrink: 0;
      color:       inherit;
    }

    .ds-btn__spinner {
      display: inline-flex;
      flex-shrink: 0;
      color: inherit;
      --ds-loading-spinner-dot-color: currentColor;
    }

    .ds-btn--m .ds-btn__spinner {
      --ds-loading-spinner-size: var(--ds-button-m-icon-size);
      --ds-loading-spinner-dot-size: calc(var(--ds-button-m-icon-size) / 6);
      --ds-loading-spinner-orbit-radius: calc(var(--ds-button-m-icon-size) / 3);
    }

    .ds-btn--s .ds-btn__spinner {
      --ds-loading-spinner-size: var(--ds-button-s-icon-size);
      --ds-loading-spinner-dot-size: calc(var(--ds-button-s-icon-size) / 6);
      --ds-loading-spinner-orbit-radius: calc(var(--ds-button-s-icon-size) / 3);
    }

    .ds-btn--xs .ds-btn__spinner {
      --ds-loading-spinner-size: var(--ds-button-xs-icon-size);
      --ds-loading-spinner-dot-size: calc(var(--ds-button-xs-icon-size) / 6);
      --ds-loading-spinner-orbit-radius: calc(var(--ds-button-xs-icon-size) / 3);
    }

    .ds-btn__label {
      letter-spacing: 0;
      line-height: var(--ds-button-label-line-height);
      font-size: var(--ds-button-label-font-size);
    }

    .ds-text--visual-padding-none {
      text-box-trim: trim-both;
    }

    /* ── Sizes ─────────────────────────────────────────────── */
    .ds-btn--m {
      height:     var(--ds-button-m-height);
      min-width:  var(--ds-button-m-min-width);
      padding:    var(--ds-button-m-padding-y) var(--ds-button-m-padding-x);
      font-size:  var(--ds-button-m-font-size);
      gap:        var(--ds-button-m-gap);
      --ds-button-label-font-size: var(--ds-button-m-font-size);
      --ds-button-label-line-height: var(--ds-button-m-line-height);
      --ds-button-resolved-border-radius: var(--ds-button-m-border-radius);
    }
    .ds-btn--s {
      height:     var(--ds-button-s-height);
      min-width:  var(--ds-button-s-min-width);
      padding:    var(--ds-button-s-padding-y) var(--ds-button-s-padding-x);
      font-size:  var(--ds-button-s-font-size);
      gap:        var(--ds-button-s-gap);
      --ds-button-label-font-size: var(--ds-button-s-font-size);
      --ds-button-label-line-height: var(--ds-button-s-line-height);
      --ds-button-resolved-border-radius: var(--ds-button-s-border-radius);
    }
    .ds-btn--xs {
      height:     var(--ds-button-xs-height);
      min-width:  var(--ds-button-xs-min-width);
      padding:    var(--ds-button-xs-padding-y) var(--ds-button-xs-padding-x);
      font-size:  var(--ds-button-xs-font-size);
      gap:        var(--ds-button-xs-gap);
      --ds-button-label-font-size: var(--ds-button-xs-font-size);
      --ds-button-label-line-height: var(--ds-button-xs-line-height);
      --ds-button-resolved-border-radius: var(--ds-button-xs-border-radius);
    }

    /* ── Emphasis — normal ─────────────────────────────────── */
    .ds-btn--primary {
      --_button-bg:     var(--ds-button-primary-background);
      color:            var(--ds-button-primary-foreground);
      --_hover-overlay: var(--ds-button-primary-hover-overlay);
    }

    .ds-btn--primary-alt {
      --_button-bg:     var(--ds-button-primary-alt-background);
      color:            var(--ds-button-primary-alt-foreground);
      --_hover-overlay: var(--ds-button-primary-alt-hover-overlay);
    }

    .ds-btn--secondary-outlined {
      --_button-bg:     var(--ds-button-secondary-outlined-background);
      color:            var(--ds-button-secondary-outlined-foreground);
      --_hover-overlay: var(--ds-button-secondary-outlined-hover-overlay);
      border:           var(--ds-button-secondary-outlined-border-width) solid currentColor;
    }

    .ds-btn--secondary-filled {
      --_button-bg:     var(--ds-button-secondary-filled-background);
      color:            var(--ds-button-secondary-filled-foreground);
      --_hover-overlay: var(--ds-button-secondary-filled-hover-overlay);
    }

    .ds-btn--terciary {
      --_button-bg:     var(--ds-button-terciary-background);
      color:            var(--ds-button-terciary-foreground);
      --_hover-overlay: var(--ds-button-terciary-hover-overlay);
    }

    .ds-btn--terciary .ds-btn__label {
      text-decoration: underline;
    }

    .ds-btn--terciary.ds-btn--has-icon .ds-btn__label {
      text-decoration: none;
    }

    /* ── Emphasis — invert ─────────────────────────────────── */
    .ds-btn--primary.ds-btn--invert {
      --_button-bg:     var(--ds-button-primary-invert-background);
      color:            var(--ds-button-primary-invert-foreground);
      --_hover-overlay: var(--ds-button-primary-invert-hover-overlay);
    }

    .ds-btn--primary-alt.ds-btn--invert {
      --_button-bg:     var(--ds-button-primary-alt-invert-background);
      color:            var(--ds-button-primary-alt-invert-foreground);
      --_hover-overlay: var(--ds-button-primary-alt-invert-hover-overlay);
    }

    .ds-btn--secondary-outlined.ds-btn--invert {
      --_button-bg:     var(--ds-button-secondary-outlined-invert-background);
      color:            var(--ds-button-secondary-outlined-invert-foreground);
      --_hover-overlay: var(--ds-button-secondary-outlined-invert-hover-overlay);
    }

    .ds-btn--secondary-filled.ds-btn--invert {
      --_button-bg:     var(--ds-button-secondary-filled-invert-background);
      color:            var(--ds-button-secondary-filled-invert-foreground);
      --_hover-overlay: var(--ds-button-secondary-filled-invert-hover-overlay);
    }

    .ds-btn--terciary.ds-btn--invert {
      --_button-bg:     var(--ds-button-terciary-invert-background);
      color:            var(--ds-button-terciary-invert-foreground);
      --_hover-overlay: var(--ds-button-terciary-invert-hover-overlay);
    }

    /* ── Disabled ───────────────────────────────────────────── */
    .ds-btn:disabled {
      opacity: 0.4;
      cursor:  not-allowed;
    }

    /* ── Focus ring ─────────────────────────────────────────── */
    .ds-btn:focus-visible {
      outline:        var(--ds-focus-stroke) solid var(--ds-focus-color);
      outline-offset: var(--ds-focus-gap);
    }
  `],
})
export class DsButtonV2Component implements AfterViewInit, AfterViewChecked, OnDestroy {
  @ViewChild('buttonElement') private buttonElement?: ElementRef<HTMLButtonElement>;

  @Input() label   = 'Button';
  @Input() emphasis: DsButtonEmphasis = 'primary';
  @Input() size: DsButtonSize = 'm';
  @Input() disabled = false;
  @Input() invert   = false;
  @Input() gradientBorder: boolean | string = false;
  @Input() leadingIcon: DsIconName | string = '';
  @Input() trailingIcon: DsIconName | string = '';
  @Input({ transform: booleanAttribute }) loading = false;
  @Input({ transform: booleanAttribute }) fullWidth = false;
  @Input() set maxWidth(value: number | string) {
    this.resolvedMaxWidth = this.toCssLength(value, 'var(--ds-button-default-max-width)');
  }

  @Output() clicked = new EventEmitter<MouseEvent>();

  resolvedMaxWidth = 'var(--ds-button-default-max-width)';

  private widthAnimationFrame: number | null = null;
  private viewReady = false;

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.syncAnimatedWidth(false);
  }

  ngAfterViewChecked(): void {
    if (!this.viewReady) {
      return;
    }

    this.scheduleAnimatedWidthSync();
  }

  ngOnDestroy(): void {
    if (this.widthAnimationFrame !== null) {
      cancelAnimationFrame(this.widthAnimationFrame);
    }
  }

  get hostClasses(): string {
    return [
      'ds-btn',
      `ds-btn--${this.size}`,
      `ds-btn--${resolveDsButtonEmphasis(this.emphasis)}`,
      this.invert ? 'ds-btn--invert' : '',
      this.hasGradientBorder ? 'ds-btn--gradient-border' : '',
      this.fullWidth ? 'ds-btn--full-width' : '',
      (this.leadingIcon || this.trailingIcon || this.loading) ? 'ds-btn--has-icon' : '',
    ].filter(Boolean).join(' ');
  }

  get hasGradientBorder(): boolean {
    return (this.gradientBorder === true || this.gradientBorder === 'true')
      && (this.emphasis === 'primary' || this.emphasis === 'primary-alt');
  }

  get iconSize(): DsIconSize {
    return this.size === 'm'
      ? 'var(--ds-button-m-icon-size)'
      : this.size === 's'
        ? 'var(--ds-button-s-icon-size)'
        : 'var(--ds-button-xs-icon-size)';
  }

  private scheduleAnimatedWidthSync(): void {
    if (this.widthAnimationFrame !== null) {
      return;
    }

    this.widthAnimationFrame = requestAnimationFrame(() => {
      this.widthAnimationFrame = null;
      this.syncAnimatedWidth(true);
    });
  }

  private syncAnimatedWidth(animate: boolean): void {
    const button = this.buttonElement?.nativeElement;
    if (!button) {
      return;
    }

    if (this.fullWidth) {
      button.style.width = '100%';
      return;
    }

    const targetWidth = this.measureNaturalWidth(button);
    if (targetWidth <= 0) {
      return;
    }

    const currentWidth = button.getBoundingClientRect().width;
    if (Math.abs(currentWidth - targetWidth) < 0.5) {
      button.style.width = `${targetWidth}px`;
      return;
    }

    if (!animate) {
      button.style.width = `${targetWidth}px`;
      return;
    }

    button.style.width = `${currentWidth}px`;
    button.getBoundingClientRect();
    button.style.width = `${targetWidth}px`;
  }

  private measureNaturalWidth(button: HTMLButtonElement): number {
    const clone = button.cloneNode(true) as HTMLButtonElement;
    clone.style.position = 'absolute';
    clone.style.visibility = 'hidden';
    clone.style.pointerEvents = 'none';
    clone.style.width = 'auto';
    clone.style.left = '-9999px';
    clone.style.top = '-9999px';
    clone.style.transition = 'none';

    button.after(clone);
    const width = clone.getBoundingClientRect().width;
    clone.remove();

    return width;
  }

  private toCssLength(value: number | string | null | undefined, fallback: string): string {
    if (typeof value === 'number') return `${value}px`;
    if (typeof value === 'string' && value.trim().length > 0) {
      const trimmed = value.trim();
      return /^\d+(\.\d+)?$/.test(trimmed) ? `${trimmed}px` : trimmed;
    }
    return fallback;
  }
}
